import React, { FC, memo, useCallback, useMemo } from 'react'
import { Card } from 'react-components'
import { AnyStyledComponent } from 'styled-components'
import { useTranslation } from '../../../hooks/locale'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { landingPageEventCategories } from '../../../services/analytics-service/categories'
import { landingPageEventActions } from '../../../services/analytics-service/actions'
import AnalyticsService from '../../../services/analytics-service'
import { getPropertyClickRedirectUrl, preventDefaultHandler } from '../../utils'
import { usePropertyCardTracking } from '../../hooks'
import { encodeToBase64 } from '../../../utils/encoding-utils'
import { isServer } from '../../../utils/platform-utils'
import get from 'lodash/get'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'

import {
    ApartmentInfoContainer,
    ApartmentRating,
    ApartmentRegion,
    ApartmentTitle,
    Container,
    FooterContainer,
    IconContainer,
    ImageContainer,
    InnerContainer,
    ReviewContainer,
    ReviewerContainer,
    ReviewText,
    StarIcon,
    TopContainer,
    UserInfo,
    SeoHref,
    RatingContainer,
    RegionSeoHref,
    ApartmentInnerContainer,
} from './styles'

import Star from '../../../assets/icons/star.svg'
import NextImage from '../../common/next-image'

interface Props {
    listing: ListingSectionObject
    position: number
    requestUrl?: string
    breadcrumbList?: BreadcrumbListTransport
}

const ReviewsCard: FC<Props> = ({ listing, requestUrl, breadcrumbList }) => {
    const { t } = useTranslation()
    const { propertyCardClickTracking, intersectionTargetRef } = usePropertyCardTracking(listing)

    const href = get(listing, 'href', '')
    const seoRegion = get(listing, 'seoRegion', {})
    const rating = get(listing, 'rating', {})
    const breadcrumbs = get(listing, 'breadcrumbs', [])
    const seoHref = get(listing, 'seoHref', '')
    const noCrawl = get(listing, 'noCrawl', false)
    const isPremiumTopObject = get(listing, 'isPremiumTopObject', false)
    const isTopObject = get(listing, 'isTopObject', false)
    const images = get(listing, 'images', [])

    const average = Number(get(rating, 'average', 0)).toFixed(1)
    const count = get(rating, 'count', 0)
    const review = get(rating, 'review', null)

    const reviewerName = useMemo(() => {
        const firstName = get(review, 'latestRating.contact.firstName', '')
        const lastName = get(review, 'latestRating.contact.lastName', '')
        return `${firstName} ${lastName}`.trim()
    }, [review])

    const handleCardClick = useCallback(() => {
        // TODO (Future): Will add sentry here for debugging object issue
        const safeHref = typeof href === 'string' ? href : ''

        const eventCategory = `${landingPageEventCategories.listingCardSection.TOP_INSERAT} - ${safeHref}`
        const eventAction = landingPageEventActions.listingCardSection.TOP_INSERAT
        const _objectURL = getPropertyClickRedirectUrl(t('traumBaseUrl'), safeHref)

        // handle traumBoost tracking
        if (isPremiumTopObject || isTopObject) {
            // handle traumBoost tracking
            // from_page & tb will only be set for all top listings in RLP
            // from_page contains base64 encoded url of the page from which the request is made
            _objectURL.searchParams.set(
                'from_page',
                isServer ? encodeToBase64(requestUrl ?? '') : encodeToBase64(window.location.href),
            )
            _objectURL.searchParams.set('tb', '1')
        }

        propertyCardClickTracking(listing, null)

        AnalyticsService.trackEvent({ action: eventAction, category: eventCategory, label: _objectURL.toString() })
        window.open(_objectURL.href, '_blank')
    }, [href, t, listing, propertyCardClickTracking])

    const formattedBreadcrumb = useMemo(() => {
        return breadcrumbList?.getLastRegionLink(breadcrumbs, '') || null
    }, [breadcrumbList, breadcrumbs])

    const regionName = get(formattedBreadcrumb, 'title') || get(seoRegion, 'name', '')
    const regionLink = get(formattedBreadcrumb, 'url') || get(seoRegion, 'url.url', '')

    const handleRegionClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation()
    }, [])

    const propertyTitle = useMemo(() => {
        const propertyType = get(listing, 'propertyType', '')
        const title = get(listing, 'title', '')
        return `${propertyType} ${title}`.trim()
    }, [listing])

    const propertyImage = get(images, '[0]', null)

    return (
        <Container ref={intersectionTargetRef} onClick={handleCardClick}>
            <Card borderRadius={'0.5rem'} cardHeight="100%">
                <InnerContainer>
                    <TopContainer>
                        <ApartmentInfoContainer>
                            <ApartmentRegion>
                                <RegionSeoHref title={regionName} href={regionLink} onClick={handleRegionClick}>
                                    {regionName}
                                </RegionSeoHref>
                            </ApartmentRegion>
                            <ApartmentInnerContainer>
                                <ApartmentTitle>
                                    <SeoHref
                                        autoRedirect={false}
                                        title={propertyTitle}
                                        href={seoHref}
                                        noCrawl={noCrawl}
                                        onClick={preventDefaultHandler}
                                    >
                                        {propertyTitle}
                                    </SeoHref>
                                </ApartmentTitle>
                                {average && (
                                    <RatingContainer>
                                        <IconContainer>
                                            <StarIcon as={Star as AnyStyledComponent} />
                                        </IconContainer>
                                        <ApartmentRating>{`${average} (${count} ${
                                            count === 1 ? t('review') : t('reviews')
                                        })`}</ApartmentRating>
                                    </RatingContainer>
                                )}
                            </ApartmentInnerContainer>
                        </ApartmentInfoContainer>
                        {!!propertyImage && (
                            <ImageContainer>
                                <NextImage
                                    src={get(propertyImage, 'url', '')}
                                    alt={get(propertyImage, 'alt', 'Property image')}
                                />
                            </ImageContainer>
                        )}
                    </TopContainer>
                    <ReviewContainer>
                        {review?.latestRating && <ReviewText>{review.latestRating.text}</ReviewText>}
                    </ReviewContainer>
                    <FooterContainer>
                        <ReviewerContainer>{!!reviewerName && <UserInfo>{reviewerName}</UserInfo>}</ReviewerContainer>
                    </FooterContainer>
                </InnerContainer>
            </Card>
        </Container>
    )
}

export default memo(ReviewsCard)
